// Fetch wrapper for the API: JSON in/out, CSRF header on state-changing requests, plain-language
// errors from the server's `{ error, message }` bodies.
import { withStream } from "./stream";

let csrfToken: string | null = null;

export function setCsrf(t: string | null): void {
  csrfToken = t;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
  }
}

export async function api<T>(method: "GET" | "POST" | "PUT" | "DELETE", path: string, body?: unknown): Promise<T> {
  // Requests are for this site's stream (the 72 BPM site adds `?bpm=72`; a `bpm` in `path` wins).
  const url = withStream(path);
  // The static demo answers in the browser (dropped from the normal build).
  if (__EB_DEMO__) return (await import("./demo/backend")).handle<T>(method, url, body);
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["x-csrf-token"] = csrfToken;
  const r = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "same-origin",
  });
  const text = await r.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!r.ok) {
    const d = (data ?? {}) as { error?: string; message?: string };
    throw new ApiError(
      r.status,
      d.error ?? "http_error",
      d.message ?? (r.status === 429 ? "Too many requests. Please wait a moment." : `Request failed (${r.status}).`),
      data,
    );
  }
  return data as T;
}
